/**
 * Create/Edit Experiment Configuration Page
 * Teachers use this to configure experiments with pre-lab, modes, and discussion
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Settings, ClipboardCheck, MessageSquare, Lock, Beaker, Loader2, CheckCircle, BookOpen, ArrowRight, ShieldAlert, AlertCircle, Droplets, Zap, Clock, Flame, FlaskConical, Sparkles } from 'lucide-react';
import ThemeToggle from './guided/titration/panels/ThemeToggle';
import { PreLabQuestionEditor } from './QuestionEditor';
import { DiscussionQuestionEditor } from './QuestionEditor';
import { useCurrentUser } from './AuthComponents';
import { useCreateExperiment, useAddPreLabQuestion, useAddDiscussionQuestion, useSetTeacherPassword, useUpdatePreLabSettings } from '../utils/convexHooks';

type ExperimentType = 'titration' | 'znH2SO4' | 'naphthalene' | 'solubility' | 'magnesiumOxide' | 'copperOxide' | 'balancedEquations';
type ExperimentMode = 'practice' | 'test';

interface PreLabQuestion {
    id: string;
    type: 'mcq' | 'true-false' | 'short-answer';
    question: string;
    options?: string[];
    correctAnswer: string | number;
    points: number;
    explanation: string;
    order: number;
}

interface DiscussionQuestion {
    id: string;
    question: string;
    guidanceNotes?: string;
    expectedKeyPoints?: string[];
    maxWords?: number;
    order: number;
}

export default function CreateExperimentPage() {
    const navigate = useNavigate();
    const { user } = useCurrentUser();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    
    // Mutations
    const createExperiment = useCreateExperiment();
    const addPreLabQuestion = useAddPreLabQuestion();
    const addDiscussionQuestion = useAddDiscussionQuestion();
    const setTeacherPasswordMutation = useSetTeacherPassword();
    const updatePreLabSettings = useUpdatePreLabSettings();
    
    // Basic Info
    const [experimentType, setExperimentType] = useState<ExperimentType>('titration');
    const [experimentTitle, setExperimentTitle] = useState('');
    
    // Pre-Lab Settings
    const [preLabEnabled, setPreLabEnabled] = useState(true);
    const [preLabQuestions, setPreLabQuestions] = useState<PreLabQuestion[]>([]);
    const [passingScore, setPassingScore] = useState(70);
    const [maxAttempts, setMaxAttempts] = useState(3);
    const [showAnswersAfterFail, setShowAnswersAfterFail] = useState(false);
    
    // Experiment Mode
    const [experimentMode, setExperimentMode] = useState<ExperimentMode>('practice');
    
    // Retake settings
    const [maxRetakes, setMaxRetakes] = useState<number | 'unlimited'>('unlimited');
    
    // Teacher Bypass
    const [allowTeacherBypass, setAllowTeacherBypass] = useState(true);
    const [teacherPassword, setTeacherPassword] = useState('');
    
    // Additional Options
    const [enableAssistant, setEnableAssistant] = useState(true);
    const [showTutorial, setShowTutorial] = useState(true);
    const [allowStudentConfig, setAllowStudentConfig] = useState(true);
    
    // Post-Lab Discussion
    const [discussionEnabled, setDiscussionEnabled] = useState(true);
    const [discussionQuestions, setDiscussionQuestions] = useState<DiscussionQuestion[]>([]);

    const handleSave = async () => {
        if (!user) {
            setSaveError('User not authenticated');
            return;
        }
        
        setIsSaving(true);
        setSaveError('');
        
        try {
            // Step 1: Create experiment
            const { experimentId } = await createExperiment({
                teacherId: user._id,
                experimentType,
                title: experimentTitle || (
                    experimentType === 'titration' ? 'Titration Experiment' :
                    experimentType === 'znH2SO4' ? 'Zinc + H2SO4 Experiment' :
                    experimentType === 'naphthalene' ? 'Naphthalene Heating/Cooling Experiment' :
                    experimentType === 'solubility' ? 'Salt Solubility Experiment' :
                    experimentType === 'magnesiumOxide' ? 'Empirical Formula of Magnesium Oxide' :
                    experimentType === 'copperOxide' ? 'Empirical Formula of Copper(II) Oxide' :
                    experimentType === 'balancedEquations' ? 'Writing and Observing Balanced Chemical Equations' :
                    'Lab Experiment'
                ),
                preLab: {
                    enabled: preLabEnabled,
                    passingScore,
                    maxAttempts,
                    showAnswersAfterFail,
                    questions: [],
                },
                experimentMode,
                autoFillData: experimentMode === 'practice',
                enableAssistant,
                showTutorial,
                allowStudentConfig,
                maxRetakes,
                postLab: {
                    enabled: discussionEnabled,
                    discussionQuestions: [],
                },
                allowTeacherBypass,
            });
            
            // Step 2: Add pre-lab questions
            if (preLabEnabled && preLabQuestions.length > 0) {
                for (let i = 0; i < preLabQuestions.length; i++) {
                    const question = preLabQuestions[i];
                    await addPreLabQuestion({
                        experimentConfigId: experimentId,
                        type: question.type,
                        question: question.question,
                        options: question.options,
                        correctAnswer: question.correctAnswer,
                        points: question.points,
                        explanation: question.explanation,
                        order: i,
                    });
                }
            }
            
            // Step 3: Set teacher password if enabled
            if (allowTeacherBypass && teacherPassword) {
                await setTeacherPasswordMutation({
                    experimentId,
                    hashedPassword: teacherPassword, // TODO: Hash on backend
                });
            }
            
            // Step 4: Add discussion questions
            if (discussionEnabled && discussionQuestions.length > 0) {
                for (let i = 0; i < discussionQuestions.length; i++) {
                    const question = discussionQuestions[i];
                    await addDiscussionQuestion({
                        experimentConfigId: experimentId,
                        question: question.question,
                        guidanceNotes: question.guidanceNotes,
                        expectedKeyPoints: question.expectedKeyPoints,
                        maxWords: question.maxWords,
                        order: i,
                    });
                }
            }
            
            // Success!
            navigate('/teacher/dashboard');
        } catch (error: any) {
            console.error('Failed to create experiment:', error);
            setSaveError(error.message || 'Failed to create experiment. Please try again.');
            setIsSaving(false);
        }
    };

    const steps = [
        { number: 1, name: 'Basic Info', icon: Beaker },
        { number: 2, name: 'Pre-Lab', icon: ClipboardCheck },
        { number: 3, name: 'Settings', icon: Settings },
        { number: 4, name: 'Discussion', icon: MessageSquare },
    ];

    return (
        <div className="min-h-screen bg-[#f0f4ff] dark:bg-[#030712] animate-in fade-in duration-700 transition-colors duration-500 selection:bg-blue-200 dark:selection:bg-violet-900">
            {/* Dark-mode aurora */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="dark:hidden absolute inset-0 bg-gradient-to-br from-blue-50/60 via-transparent to-teal-50/40" />
                <div className="hidden dark:block absolute -top-1/3 -right-1/4 w-[50vw] h-[50vw] rounded-full animate-aurora"
                    style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 65%)' }} />
            </div>

            {/* Header */}
            <header className="relative z-50 sticky top-0 bg-white/80 dark:bg-[#030712]/80 backdrop-blur-xl border-b border-blue-100/80 dark:border-violet-500/10 transition-all">
                <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-3.5">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/teacher/dashboard')}
                                className="p-2 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all border border-slate-200/50 dark:border-violet-500/10 shrink-0 flex"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            </button>
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight font-space leading-none">
                                    Lab Configuration
                                </h1>
                                <p className="text-[11px] font-semibold text-blue-500 dark:text-violet-400 uppercase tracking-widest leading-none mt-0.5">
                                    New Experiment
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <ThemeToggle />
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="group flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm font-space text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 dark:hover:from-violet-500 dark:hover:to-cyan-400 shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25"
                            >
                                {isSaving ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Deploying…</>
                                ) : (
                                    <><Save className="w-4 h-4" /> Deploy Module</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="relative z-10 w-full h-[calc(100vh-80px)] flex flex-col animate-in slide-in-from-bottom-8 duration-700 ease-out">
                {/* Step Progress Bar */}
                <div className="shrink-0 py-5 px-4 sm:px-6 lg:px-8 xl:px-12 w-full border-b border-blue-100/60 dark:border-violet-500/10 bg-white/60 dark:bg-[#030712]/60 backdrop-blur-md">
                    <div className="flex items-center justify-between max-w-2xl mx-auto">
                        {steps.map((step, index) => (
                            <div key={step.number} className="flex items-center flex-1">
                                <button
                                    className="flex flex-col items-center gap-1.5 cursor-pointer group focus:outline-none"
                                    onClick={() => setCurrentStep(step.number)}
                                >
                                    {/* Circle */}
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm font-space transition-all duration-400 ${
                                        currentStep === step.number
                                            ? 'bg-blue-600 dark:bg-gradient-to-br dark:from-violet-600 dark:to-cyan-500 text-white shadow-lg shadow-blue-500/30 dark:shadow-violet-500/30 scale-110'
                                            : currentStep > step.number
                                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700'
                                            : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-violet-500/10'
                                    }`}>
                                        {currentStep > step.number
                                            ? <CheckCircle className="w-5 h-5" />
                                            : step.number}
                                    </div>
                                    {/* Label */}
                                    <span className={`text-[10px] font-bold uppercase tracking-wider font-space hidden sm:block whitespace-nowrap transition-colors ${
                                        currentStep === step.number
                                            ? 'text-blue-600 dark:text-violet-400'
                                            : currentStep > step.number
                                            ? 'text-emerald-500 dark:text-emerald-400'
                                            : 'text-slate-400 dark:text-slate-600'
                                    }`}>{step.name}</span>
                                </button>

                                {index < steps.length - 1 && (
                                    <div className="flex-1 h-0.5 mx-2 sm:mx-3 rounded-full overflow-hidden bg-slate-200 dark:bg-white/8">
                                        <div
                                            className="h-full bg-gradient-to-r from-emerald-400 to-blue-400 dark:from-emerald-500 dark:to-violet-500 transition-all duration-700 ease-out"
                                            style={{ width: currentStep > step.number ? '100%' : '0%' }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex-1 w-full min-h-0 flex flex-col">
                    <div className="flex-1 flex flex-col w-full h-full">
                        <div className="flex-1 p-4 sm:p-8 lg:p-10 xl:p-12 w-full h-full overflow-y-auto">
                            <div className="relative w-full h-full animate-in fade-in duration-500 max-w-none">
                                {saveError && (
                                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/30 rounded-xl flex items-center gap-3 text-red-700 dark:text-red-400 text-sm font-semibold">
                                        <AlertCircle className="w-5 h-5 shrink-0" />
                                        <p>{saveError}</p>
                                    </div>
                                )}

                                {currentStep === 1 && (
                                    <BasicInfoStep
                                        experimentType={experimentType}
                                        setExperimentType={setExperimentType}
                                        experimentTitle={experimentTitle}
                                        setExperimentTitle={setExperimentTitle}
                                        onNext={() => setCurrentStep(2)}
                                    />
                                )}

                                {currentStep === 2 && (
                                    <PreLabStep
                                        enabled={preLabEnabled}
                                        setEnabled={setPreLabEnabled}
                                        questions={preLabQuestions}
                                        setQuestions={setPreLabQuestions}
                                        passingScore={passingScore}
                                        setPassingScore={setPassingScore}
                                        maxAttempts={maxAttempts}
                                        setMaxAttempts={setMaxAttempts}
                                        showAnswersAfterFail={showAnswersAfterFail}
                                        setShowAnswersAfterFail={setShowAnswersAfterFail}
                                        experimentType={experimentType}
                                        onNext={() => setCurrentStep(3)}
                                        onBack={() => setCurrentStep(1)}
                                    />
                                )}

                                {currentStep === 3 && (
                                    <SettingsStep
                                        experimentMode={experimentMode}
                                        setExperimentMode={setExperimentMode}
                                        maxRetakes={maxRetakes}
                                        setMaxRetakes={setMaxRetakes}
                                        allowTeacherBypass={allowTeacherBypass}
                                        setAllowTeacherBypass={setAllowTeacherBypass}
                                        teacherPassword={teacherPassword}
                                        setTeacherPassword={setTeacherPassword}
                                        enableAssistant={enableAssistant}
                                        setEnableAssistant={setEnableAssistant}
                                        showTutorial={showTutorial}
                                        setShowTutorial={setShowTutorial}
                                        allowStudentConfig={allowStudentConfig}
                                        setAllowStudentConfig={setAllowStudentConfig}
                                        onNext={() => setCurrentStep(4)}
                                        onBack={() => setCurrentStep(2)}
                                    />
                                )}

                                {currentStep === 4 && (
                                    <DiscussionStep
                                        enabled={discussionEnabled}
                                        setEnabled={setDiscussionEnabled}
                                        questions={discussionQuestions}
                                        setQuestions={setDiscussionQuestions}
                                        experimentType={experimentType}
                                        onBack={() => setCurrentStep(3)}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Navigation Footer */}
                        <div className="shrink-0 px-4 sm:px-8 lg:px-10 xl:px-12 py-4 bg-white/70 dark:bg-[#030712]/70 backdrop-blur-xl border-t border-blue-100/60 dark:border-violet-500/10 flex justify-between items-center w-full gap-3">
                            <button
                                onClick={() => currentStep > 1 && setCurrentStep(currentStep - 1)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm font-space transition-all duration-200 border ${
                                    currentStep === 1
                                    ? 'opacity-0 pointer-events-none border-transparent'
                                    : 'bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-violet-500/15 hover:bg-slate-50 dark:hover:bg-white/10 hover:-translate-y-0.5 shadow-sm'
                                }`}
                            >
                                <ArrowLeft className="w-4 h-4" /> Previous
                            </button>

                            <button
                                onClick={() => currentStep < 4 ? setCurrentStep(currentStep + 1) : handleSave()}
                                disabled={isSaving}
                                className="group flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm font-space text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 dark:hover:from-violet-500 dark:hover:to-cyan-400 shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25"
                            >
                                {currentStep === 4 ? (
                                    isSaving
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying…</>
                                        : <><CheckCircle className="w-4 h-4" /> Deploy Lab</>
                                ) : (
                                    <>Next Step <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </main>
        </div>
    );
}

// Step 1: Basic Info
function BasicInfoStep({
    experimentType,
    setExperimentType,
    experimentTitle,
    setExperimentTitle,
    onNext,
}: {
    experimentType: ExperimentType;
    setExperimentType: (type: ExperimentType) => void;
    experimentTitle: string;
    setExperimentTitle: (title: string) => void;
    onNext: () => void;
}) {
    const experiments = [
        {
            id: 'titration',
            title: 'Acid-Base Titration (HCl vs NaOH)',
            category: 'Chemistry',
            description: 'Determine the concentration of an unknown acid or base through neutralization.',
            icon: <Droplets className="w-8 h-8 text-slate-900 dark:text-slate-100" />,
        },
        {
            id: 'znH2SO4',
            title: 'Rate of Reaction (Zinc + Sulfuric Acid)',
            category: 'Kinetics',
            description: 'Investigate how factors affect reaction rates using gas collection.',
            icon: <Zap className="w-8 h-8 text-amber-500" />,
        },
        {
            id: 'naphthalene',
            title: 'Melting/Freezing of Naphthalene',
            category: 'Thermodynamics',
            description: 'Determine the melting point and freezing point of naphthalene.',
            icon: <Flame className="w-8 h-8 text-orange-500" />,
        },
        {
            id: 'solubility',
            title: 'Effect of Temperature on Salt Solubility',
            category: 'Chemistry',
            description: 'Study how water temperature (10°C, 30°C, 80°C) affects how much salt dissolves.',
            icon: <FlaskConical className="w-8 h-8 text-cyan-500" />,
        },
        {
            id: 'magnesiumOxide',
            title: 'Empirical Formula of Magnesium Oxide',
            category: 'Chemistry',
            description: 'Combust magnesium ribbon in a crucible and determine the empirical formula of MgO from mass changes.',
            icon: <Sparkles className="w-8 h-8 text-amber-500" />,
        },
        {
            id: 'copperOxide',
            title: 'Empirical Formula of Copper(II) Oxide',
            category: 'Chemistry',
            description: 'Reduce CuO powder with hydrogen gas in a glass tube and determine the empirical formula of CuO from mass changes.',
            icon: <Sparkles className="w-8 h-8 text-rose-500" />,
        },
        {
            id: 'balancedEquations',
            title: 'Writing & Observing Balanced Chemical Equations',
            category: 'Chemistry',
            description: 'Observe three reactions (CuCO₃ heating, HCl+NH₃ smoke, Pb(NO₃)₂+KI precipitate) and write the balanced equation for each.',
            icon: <FlaskConical className="w-8 h-8 text-fuchsia-500" />,
        },
        {
            id: 'pendulum',
            title: 'Simple Pendulum Oscillations',
            category: 'Physics',
            description: 'Measure gravity and understand harmonic motion. (Coming Soon)',
            icon: <Clock className="w-8 h-8 text-slate-500" />,
            disabled: true,
        },
    ];

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl">
            <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white font-space tracking-tight mb-1">
                    Experiment Setup
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Configure the experiment type and title for your students' virtual lab session.</p>
            </div>

            <div className="space-y-3">
                <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 font-space">
                    1. Select Experiment Module
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {experiments.map((exp) => (
                        <button
                            key={exp.id}
                            disabled={exp.disabled}
                            onClick={() => setExperimentType(exp.id as ExperimentType)}
                            className={`relative text-left p-5 rounded-2xl border-2 transition-all duration-300 focus:outline-none ${
                                experimentType === exp.id
                                    ? 'border-blue-500 dark:border-violet-500 bg-blue-50/80 dark:bg-violet-900/15 shadow-lg shadow-blue-500/10 dark:shadow-violet-500/10'
                                    : exp.disabled
                                    ? 'border-slate-200 dark:border-white/5 opacity-45 cursor-not-allowed bg-slate-50 dark:bg-white/[0.02]'
                                    : 'border-slate-200 dark:border-violet-500/10 bg-white dark:bg-[#0d0d1a] hover:border-blue-300 dark:hover:border-violet-500/30 hover:shadow-md hover:-translate-y-0.5'
                            }`}
                        >
                            {experimentType === exp.id && (
                                <div className="absolute top-3.5 right-3.5 text-blue-600 dark:text-violet-400">
                                    <CheckCircle className="w-5 h-5" />
                                </div>
                            )}
                            <div className="mb-3">{exp.icon}</div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 mb-1 font-space">
                                {exp.category}
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1.5 line-clamp-2 font-space">
                                {exp.title}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
                                {exp.description}
                            </p>
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-3 pt-5 border-t border-slate-200 dark:border-violet-500/10">
                <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 font-space">
                    2. Assignment Details
                </label>
                <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 font-space">
                        Display Title to Students
                    </label>
                    <input
                        type="text"
                        value={experimentTitle}
                        onChange={(e) => setExperimentTitle(e.target.value)}
                        placeholder="e.g., Week 3: Titration Practical Test"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-violet-500/20 bg-white dark:bg-[#12121f] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none focus:border-blue-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-violet-500/15 transition-all font-medium text-sm"
                    />
                </div>
            </div>
        </div>
    );
}

// Step 2: Pre-Lab Assessment
const DEFAULT_PRELAB_QUESTIONS: PreLabQuestion[] = [
    {
        id: 'pre-1',
        type: 'mcq',
        question: 'What is the primary purpose of an acid-base titration?',
        options: ['To determine the concentration of an unknown solution', 'To mix two colorful liquids', 'To change the temperature of the solution', 'To dissolve a solid faster'],
        correctAnswer: 0,
        points: 10,
        explanation: 'Titration is a quantitative analytical method used to determine the unknown concentration of an identified analyte.',
        order: 0
    },
    {
        id: 'pre-2',
        type: 'true-false',
        question: 'Phenolphthalein indicator is pink in acidic solutions and colorless in basic solutions.',
        options: ['True', 'False'],
        correctAnswer: 1, // 1 corresponds to False
        points: 10,
        explanation: 'It is the opposite: Phenolphthalein is colorless in acidic solutions and turns pink in basic solutions.',
        order: 1
    },
    {
        id: 'pre-3',
        type: 'mcq',
        question: 'When reading a burette containing a clear liquid, where should you take the measurement?',
        options: ['At the very top edges of the liquid', 'At the bottom of the meniscus', 'In the middle of the liquid column', 'Anywhere as long as it is consistent'],
        correctAnswer: 1,
        points: 10,
        explanation: 'For clear liquids, measurements should always be taken at the bottom of the meniscus at eye level.',
        order: 2
    },
    {
        id: 'pre-4',
        type: 'true-false',
        question: 'A neutralization reaction between a strong acid and strong base produces water and a salt.',
        options: ['True', 'False'],
        correctAnswer: 0, // 0 corresponds to True
        points: 10,
        explanation: 'The hydrogen ions from the acid and hydroxide ions from the base combine to form water, while the remaining ions form a salt.',
        order: 3
    },
    {
        id: 'pre-5',
        type: 'mcq',
        question: 'What is the term for the point in a titration at which the indicator changes color?',
        options: ['Equivalence point', 'Midpoint', 'Endpoint', 'Starting point'],
        correctAnswer: 2,
        points: 10,
        explanation: 'The endpoint is when the indicator changes color, which is naturally chosen to be as close as possible to the equivalence point.',
        order: 4
    }
];

const DEFAULT_PRELAB_QUESTIONS_MAGNESIUM_OXIDE: PreLabQuestion[] = [
    {
        id: 'mgo-pre-1',
        type: 'short-answer',
        question: 'What is the purpose of rubbing the magnesium ribbon with sandpaper before using it?',
        correctAnswer: 'To remove the oxide layer on the surface of magnesium so that the mass of pure magnesium can be measured accurately.',
        points: 10,
        explanation: 'Rubbing removes the dull magnesium oxide coating, so only pure magnesium is weighed and the empirical formula calculation is accurate.',
        order: 0
    },
    {
        id: 'mgo-pre-2',
        type: 'short-answer',
        question: 'Name the white fumes that are produced.',
        correctAnswer: 'Magnesium oxide, MgO.',
        points: 10,
        explanation: 'The bright white fumes formed when magnesium burns in air are magnesium oxide (MgO).',
        order: 1
    },
    {
        id: 'mgo-pre-3',
        type: 'short-answer',
        question: 'Why is the lid lifted briefly during heating?',
        correctAnswer: 'To allow oxygen from the air to enter so that the magnesium burns completely.',
        points: 10,
        explanation: 'Lifting the lid lets fresh oxygen in to react with the magnesium while keeping the white MgO fumes mostly trapped inside the crucible.',
        order: 2
    },
    {
        id: 'mgo-pre-4',
        type: 'short-answer',
        question: 'Why must heating, cooling and weighing be repeated until a constant mass?',
        correctAnswer: 'To ensure all the magnesium has completely reacted with oxygen, giving an accurate result.',
        points: 10,
        explanation: 'A constant mass shows the reaction is complete — no further increase in mass means all magnesium has been converted to MgO.',
        order: 3
    }
];

const DEFAULT_PRELAB_QUESTIONS_COPPER_OXIDE: PreLabQuestion[] = [
    {
        id: 'cuo-pre-1',
        type: 'short-answer',
        question: 'What is the purpose of using zinc granules and hydrochloric acid in this activity?',
        correctAnswer: 'To generate hydrogen gas, which acts as the reducing agent to reduce copper(II) oxide to copper.',
        points: 10,
        explanation: 'Zinc reacts with hydrochloric acid (Zn + 2 HCl → ZnCl₂ + H₂) to produce hydrogen gas. The H₂ gas reduces CuO to Cu by removing the oxygen.',
        order: 0
    },
    {
        id: 'cuo-pre-2',
        type: 'short-answer',
        question: 'Why does the hydrogen gas need to flow continuously for a while before starting the heating process?',
        correctAnswer: 'To displace all the air inside the glass tube, preventing an explosion when the tube is heated.',
        points: 10,
        explanation: 'Air contains oxygen. If hydrogen is heated while mixed with air, the H₂/O₂ mixture can ignite explosively. Purging the apparatus with H₂ for at least 10 seconds first removes that oxygen.',
        order: 1
    },
    {
        id: 'cuo-pre-3',
        type: 'short-answer',
        question: 'Why is the hydrogen gas allowed to flow until the product of heating is at room temperature?',
        correctAnswer: 'To prevent the hot copper from being re-oxidised by oxygen in the air.',
        points: 10,
        explanation: 'Hot copper reacts readily with atmospheric O₂ and turns back into CuO. Keeping H₂ flowing during cooling sweeps oxygen out of the tube until the copper is cool enough to be stable.',
        order: 2
    },
    {
        id: 'cuo-pre-4',
        type: 'short-answer',
        question: 'Why do the heating, cooling and weighing processes need to be repeated until a constant mass is obtained?',
        correctAnswer: 'To ensure all the copper(II) oxide has been completely reduced to copper, giving an accurate mass.',
        points: 10,
        explanation: 'A constant mass between successive reheats means no further oxygen is being removed — the reduction is complete and the final mass accurately represents pure copper.',
        order: 3
    }
];

const DEFAULT_PRELAB_QUESTIONS_BALANCED_EQUATIONS: PreLabQuestion[] = [
    {
        id: 'be-pre-1',
        type: 'short-answer',
        question: 'What is the chemical equation for Part A (heating CuCO₃)?',
        correctAnswer: 'CuCO3(s) → CuO(s) + CO2(g). The green CuCO3 turns black (CuO) and CO2 gas turns limewater milky.',
        points: 10,
        explanation: 'Heating copper(II) carbonate is a thermal decomposition: it splits into the metal oxide (CuO, black solid) and carbon dioxide gas. The CO₂ turns limewater milky, confirming the gas\'s identity.',
        order: 0
    },
    {
        id: 'be-pre-2',
        type: 'short-answer',
        question: 'What is the chemical equation for Part B (HCl + NH₃)?',
        correctAnswer: 'HCl(g) + NH3(g) → NH4Cl(s). White smoke of ammonium chloride forms.',
        points: 10,
        explanation: 'HCl and NH₃ are both volatile — their vapours meet in the air between the tubes and react instantly to form solid NH₄Cl, visible as dense white smoke.',
        order: 1
    },
    {
        id: 'be-pre-3',
        type: 'short-answer',
        question: 'What is the chemical equation for Part C (Pb(NO₃)₂ + KI)?',
        correctAnswer: 'Pb(NO3)2(aq) + 2KI(aq) → PbI2(s) + 2KNO3(aq). Bright yellow precipitate of lead(II) iodide forms.',
        points: 10,
        explanation: 'A double-displacement (metathesis) reaction. Pb²⁺ and I⁻ form an insoluble compound (PbI₂) that drops out of solution as a yellow precipitate; K⁺ and NO₃⁻ remain dissolved.',
        order: 2
    }
];

function getDefaultPreLabQuestions(experimentType: ExperimentType): PreLabQuestion[] {
    if (experimentType === 'magnesiumOxide') return DEFAULT_PRELAB_QUESTIONS_MAGNESIUM_OXIDE;
    if (experimentType === 'copperOxide') return DEFAULT_PRELAB_QUESTIONS_COPPER_OXIDE;
    if (experimentType === 'balancedEquations') return DEFAULT_PRELAB_QUESTIONS_BALANCED_EQUATIONS;
    return DEFAULT_PRELAB_QUESTIONS;
}

function PreLabStep({
    enabled,
    setEnabled,
    questions,
    setQuestions,
    passingScore,
    setPassingScore,
    maxAttempts,
    setMaxAttempts,
    showAnswersAfterFail,
    setShowAnswersAfterFail,
    experimentType,
    onNext,
    onBack,
}: {
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    questions: PreLabQuestion[];
    setQuestions: (questions: PreLabQuestion[]) => void;
    passingScore: number;
    setPassingScore: (score: number) => void;
    maxAttempts: number;
    setMaxAttempts: (attempts: number) => void;
    showAnswersAfterFail: boolean;
    setShowAnswersAfterFail: (show: boolean) => void;
    experimentType: ExperimentType;
    onNext: () => void;
    onBack: () => void;
}) {
    const templateQuestions = getDefaultPreLabQuestions(experimentType);
    const handleLoadTemplate = () => {
        setQuestions(templateQuestions.map(q => ({ ...q, id: Date.now().toString() + Math.random().toString() })));
    };

    return (
        <div className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white font-space tracking-tight mb-1">
                        Pre-Lab Assessment
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Gate students with a knowledge check before they enter the lab simulation.
                    </p>
                </div>
                <label className="flex items-center gap-3 cursor-pointer bg-white dark:bg-white/5 p-3 rounded-xl border border-slate-200 dark:border-violet-500/15 hover:border-blue-300 dark:hover:border-violet-500/30 transition-all shrink-0">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500"
                    />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 font-space whitespace-nowrap">
                        Enable Pre-Lab
                    </span>
                </label>
            </div>

            {enabled && (
                <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 p-5 bg-white dark:bg-[#0d0d1a] rounded-2xl border border-slate-200 dark:border-violet-500/10">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 mb-2 font-space">
                                Passing Threshold (%)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={passingScore}
                                onChange={(e) => setPassingScore(Number(e.target.value))}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-violet-500/20 bg-slate-50 dark:bg-[#12121f] text-slate-900 dark:text-white font-bold text-xl focus:outline-none focus:border-blue-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-violet-500/15 transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 mb-2 font-space">
                                Max Attempts
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={maxAttempts}
                                onChange={(e) => setMaxAttempts(Number(e.target.value))}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-violet-500/20 bg-slate-50 dark:bg-[#12121f] text-slate-900 dark:text-white font-bold text-xl focus:outline-none focus:border-blue-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-violet-500/15 transition-all"
                            />
                        </div>

                        <div className="flex items-center sm:pt-6">
                            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 dark:bg-white/[0.03] hover:bg-slate-100 dark:hover:bg-white/5 transition-colors w-full border border-slate-200 dark:border-violet-500/10">
                                <input
                                    type="checkbox"
                                    checked={showAnswersAfterFail}
                                    onChange={(e) => setShowAnswersAfterFail(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500"
                                />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-space">
                                    Reveal Answers on Lockout
                                </span>
                            </label>
                        </div>
                    </div>

                    {questions.length === 0 && (
                        <div className="p-8 border-2 border-dashed border-slate-300 dark:border-violet-500/15 rounded-2xl bg-slate-50 dark:bg-white/[0.02] flex flex-col items-center text-center group transition-all hover:border-blue-400 dark:hover:border-violet-500/30">
                            <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-violet-900/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                <ClipboardCheck className="w-7 h-7 text-blue-500 dark:text-violet-400" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 font-space">No Questions Yet</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 max-w-sm">
                                Add questions manually using the editor below, or load our pre-built {templateQuestions.length}-question template.
                            </p>
                            <button
                                onClick={handleLoadTemplate}
                                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 dark:hover:from-violet-500 dark:hover:to-cyan-400 text-white rounded-xl font-semibold text-sm font-space shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25 hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <Beaker className="w-4 h-4" />
                                Load Template ({templateQuestions.length} Questions)
                            </button>
                        </div>
                    )}

                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                        <PreLabQuestionEditor
                            questions={questions}
                            onChange={setQuestions}
                        />
                    </div>
                </div>
            )}

            {!enabled && (
                <div className="text-center py-16 px-4 rounded-2xl bg-gray-50/50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 border-dashed animate-in fade-in duration-300">
                    <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4 border border-gray-200 dark:border-gray-700">
                        <ShieldAlert className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Assessment Bypassed</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                        Students will bypass theoretical checks and enter the virtual laboratory directly.
                    </p>
                </div>
            )}
        </div>
    );
}

// Step 3: Settings (Mode & Teacher Bypass)
function SettingsStep({
    experimentMode,
    setExperimentMode,
    maxRetakes,
    setMaxRetakes,
    allowTeacherBypass,
    setAllowTeacherBypass,
    teacherPassword,
    setTeacherPassword,
    enableAssistant,
    setEnableAssistant,
    showTutorial,
    setShowTutorial,
    allowStudentConfig,
    setAllowStudentConfig,
    onNext,
    onBack,
}: {
    experimentMode: ExperimentMode;
    setExperimentMode: (mode: ExperimentMode) => void;
    maxRetakes: number | 'unlimited';
    setMaxRetakes: (val: number | 'unlimited') => void;
    allowTeacherBypass: boolean;
    setAllowTeacherBypass: (allow: boolean) => void;
    teacherPassword: string;
    setTeacherPassword: (password: string) => void;
    enableAssistant: boolean;
    setEnableAssistant: (enable: boolean) => void;
    showTutorial: boolean;
    setShowTutorial: (show: boolean) => void;
    allowStudentConfig: boolean;
    setAllowStudentConfig: (allow: boolean) => void;
    onNext: () => void;
    onBack: () => void;
}) {
    return (
        <div className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl">
            <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white font-space tracking-tight mb-1">
                    Experiment Parameters
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Configure strictness, data recording mode, and lab features.</p>
            </div>

            <div className="space-y-3">
                <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 font-space">
                    Lab Mode
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        onClick={() => setExperimentMode('practice')}
                        className={`text-left p-5 rounded-2xl border-2 transition-all duration-300 relative focus:outline-none hover:-translate-y-0.5 ${
                            experimentMode === 'practice'
                                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 shadow-lg shadow-emerald-500/10'
                                : 'border-slate-200 dark:border-violet-500/10 bg-white dark:bg-[#0d0d1a] hover:border-emerald-400 dark:hover:border-emerald-500/40'
                        }`}
                    >
                        {experimentMode === 'practice' && (
                            <div className="absolute top-4 right-4 text-emerald-500"><CheckCircle className="w-5 h-5" /></div>
                        )}
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2.5 font-space">
                            <div className={`p-1.5 rounded-lg ${experimentMode === 'practice' ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-500'}`}>
                                <BookOpen className="w-4 h-4" />
                            </div>
                            Practice & Guided
                        </h3>
                        <ul className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
                            {['Automatic data entry logic', 'Step-by-step guidance', 'Focus on observation'].map(f => (
                                <li key={f} className="flex items-center gap-2">
                                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${experimentMode === 'practice' ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </button>

                    <button
                        onClick={() => { setExperimentMode('test'); setEnableAssistant(false); setShowTutorial(false); setAllowStudentConfig(false); setAllowTeacherBypass(false); }}
                        className={`text-left p-5 rounded-2xl border-2 transition-all duration-300 relative focus:outline-none hover:-translate-y-0.5 ${
                            experimentMode === 'test'
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-lg shadow-amber-500/10'
                                : 'border-slate-200 dark:border-violet-500/10 bg-white dark:bg-[#0d0d1a] hover:border-amber-400 dark:hover:border-amber-500/40'
                        }`}
                    >
                        {experimentMode === 'test' && (
                            <div className="absolute top-4 right-4 text-amber-500"><CheckCircle className="w-5 h-5" /></div>
                        )}
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2.5 font-space">
                            <div className={`p-1.5 rounded-lg ${experimentMode === 'test' ? 'bg-amber-500 text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-500'}`}>
                                <ShieldAlert className="w-4 h-4" />
                            </div>
                            Practical Test
                        </h3>
                        <ul className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
                            {['Manual data entry required', 'Strict grading on readings', 'Assistance & hints disabled'].map(f => (
                                <li key={f} className="flex items-center gap-2">
                                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${experimentMode === 'test' ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </button>
                </div>
            </div>

            <div className="space-y-3 pt-5 border-t border-slate-200 dark:border-violet-500/10">
                <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 font-space">
                    Retake Settings
                </label>
                <div className="bg-white dark:bg-[#0d0d1a] border border-slate-200 dark:border-violet-500/10 rounded-2xl p-5 sm:p-6 shadow-sm">
                    <div className="flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white font-space flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-blue-500 dark:text-violet-400" />
                                    Allowed Experiment Retakes
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    How many times can a student restart the lab if they fail or want to improve their score?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-1 flex-wrap">
                            <button
                                onClick={() => setMaxRetakes(0)}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all border ${
                                    maxRetakes === 0
                                        ? 'bg-blue-600 text-white border-blue-600 dark:bg-violet-600 dark:border-violet-600 shadow-md shadow-blue-500/20 font-space'
                                        : 'bg-transparent text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-violet-500/60'
                                }`}
                            >
                                0 Retakes (Strict)
                            </button>
                            <button
                                onClick={() => setMaxRetakes(1)}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all border ${
                                    maxRetakes === 1
                                        ? 'bg-blue-600 text-white border-blue-600 dark:bg-violet-600 dark:border-violet-600 shadow-md shadow-blue-500/20 font-space'
                                        : 'bg-transparent text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-violet-500/60'
                                }`}
                            >
                                1 Retake
                            </button>
                            <button
                                onClick={() => setMaxRetakes(3)}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all border ${
                                    maxRetakes === 3
                                        ? 'bg-blue-600 text-white border-blue-600 dark:bg-violet-600 dark:border-violet-600 shadow-md shadow-blue-500/20 font-space'
                                        : 'bg-transparent text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-violet-500/60'
                                }`}
                            >
                                3 Retakes
                            </button>
                            <button
                                onClick={() => setMaxRetakes('unlimited')}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all border flex items-center gap-1.5 ${
                                    maxRetakes === 'unlimited'
                                        ? 'bg-blue-600 text-white border-blue-600 dark:bg-violet-600 dark:border-violet-600 shadow-md shadow-blue-500/20 font-space'
                                        : 'bg-transparent text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-violet-500/60'
                                }`}
                            >
                                <span className="text-lg leading-none mt-0.5">∞</span> Unlimited
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-3 pt-5 border-t border-slate-200 dark:border-violet-500/10">
                <label className="block text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-violet-400 font-space">
                    Lab Features
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className={`flex items-start gap-3.5 p-4 rounded-xl cursor-pointer border transition-all hover:-translate-y-0.5 ${enableAssistant ? 'bg-blue-50 dark:bg-violet-900/15 border-blue-300 dark:border-violet-500/30' : 'bg-white dark:bg-[#0d0d1a] border-slate-200 dark:border-violet-500/10 hover:border-blue-200 dark:hover:border-violet-500/20'}`}>
                        <div className="pt-0.5">
                            <input type="checkbox" checked={enableAssistant || experimentMode === 'practice'} onChange={(e) => setEnableAssistant(e.target.checked)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500" />
                        </div>
                        <div>
                            <span className="font-semibold text-slate-900 dark:text-white block mb-0.5 text-sm font-space">AI Teaching Assistant</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Enable interactive AI guidance during practical sessions.</span>
                        </div>
                    </label>

                    <label className={`flex items-start gap-3.5 p-4 rounded-xl cursor-pointer border transition-all hover:-translate-y-0.5 ${showTutorial ? 'bg-blue-50 dark:bg-violet-900/15 border-blue-300 dark:border-violet-500/30' : 'bg-white dark:bg-[#0d0d1a] border-slate-200 dark:border-violet-500/10 hover:border-blue-200 dark:hover:border-violet-500/20'}`}>
                        <div className="pt-0.5">
                            <input type="checkbox" checked={showTutorial} onChange={(e) => setShowTutorial(e.target.checked)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500" />
                        </div>
                        <div>
                            <span className="font-semibold text-slate-900 dark:text-white block mb-0.5 text-sm font-space">Onboarding Sequence</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Force interface onboarding before the session starts.</span>
                        </div>
                    </label>

                    <label className={`flex items-start gap-3.5 p-4 rounded-xl cursor-pointer border transition-all hover:-translate-y-0.5 ${allowStudentConfig ? 'bg-blue-50 dark:bg-violet-900/15 border-blue-300 dark:border-violet-500/30' : 'bg-white dark:bg-[#0d0d1a] border-slate-200 dark:border-violet-500/10 hover:border-blue-200 dark:hover:border-violet-500/20'}`}>
                        <div className="pt-0.5">
                            <input type="checkbox" checked={allowStudentConfig} onChange={(e) => setAllowStudentConfig(e.target.checked)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500" />
                        </div>
                        <div>
                            <span className="font-semibold text-slate-900 dark:text-white block mb-0.5 text-sm font-space">Variable Synthesis</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Allow students to adjust initial concentration settings.</span>
                        </div>
                    </label>

                    <label className={`flex items-start gap-3.5 p-4 rounded-xl cursor-pointer border transition-all hover:-translate-y-0.5 ${allowTeacherBypass ? 'bg-blue-50 dark:bg-violet-900/15 border-blue-300 dark:border-violet-500/30' : 'bg-white dark:bg-[#0d0d1a] border-slate-200 dark:border-violet-500/10 hover:border-blue-200 dark:hover:border-violet-500/20'}`}>
                        <div className="pt-0.5">
                            <input type="checkbox" checked={allowTeacherBypass} onChange={(e) => setAllowTeacherBypass(e.target.checked)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500" />
                        </div>
                        <div className="flex-1">
                            <span className="font-semibold text-slate-900 dark:text-white block mb-0.5 text-sm font-space">Instructor Password Override</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Allow grading override for instructors and proctors.</span>

                            {allowTeacherBypass && (
                                <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300" onClick={(e) => e.stopPropagation()}>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                                        <input
                                            type="password"
                                            value={teacherPassword}
                                            onChange={(e) => setTeacherPassword(e.target.value)}
                                            placeholder="Enter override password…"
                                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-violet-500/20 bg-white dark:bg-[#12121f] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none focus:border-blue-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-violet-500/15 transition-all text-sm font-mono"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </label>
                </div>
            </div>
        </div>
    );
}

// Step 4: Discussion Questions
const DEFAULT_DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
    {
        id: 'disc-1',
        question: 'Why is it necessary to rinse the burette with the titrant (e.g., standard NaOH solution) before filling it, rather than just using distilled water?',
        expectedKeyPoints: ['Distilled water would dilute the titrant', 'Changes the actual concentration of the titrant', 'Leads to inaccurate volume readings and erroneous molarity calculations'],
        guidanceNotes: 'Students should mention concentration dilution as the primary reason. Award full marks if they link the dilution directly to the final concentration calculation.',
        maxWords: 100,
        order: 0
    },
    {
        id: 'disc-2',
        question: 'Explain the effect on your final calculated concentration of HCl if the tip of the burette was not properly filled (i.e., contained an air bubble) before starting the titration, but was filled after the titration ended.',
        expectedKeyPoints: ['The air bubble takes up volume initially', 'Initial reading seems lower than actual liquid level', 'Calculated volume used will be larger than the actual volume dispensed', 'Final calculated concentration of acid will be inaccurately low'],
        guidanceNotes: 'Look for the logical sequence: Air bubble → Higher recorded volume dispensed → Affects M1V1=M2V2 calculation.',
        maxWords: 150,
        order: 1
    },
    {
        id: 'disc-3',
        question: 'In this experiment, replacing a strong acid (HCl) with a weak acid (e.g., ethanoic acid, CH3COOH) would require a specific indicator. Explain why Phenolphthalein is suitable for this weak acid-strong base titration, but Methyl Orange would not be.',
        expectedKeyPoints: ['Weak acid-strong base titration has an equivalence point pH > 7 (around pH 8-9)', 'Phenolphthalein changes color in the basic range (pH 8.2-10.0)', 'Methyl orange changes color in the acidic range (pH 3.1-4.4)', 'The chosen indicator must change color at the steep part of the titration curve (endpoint)'],
        guidanceNotes: 'Core Malaysia Foundation/Matrikulasi syllabus question testing indicator selection based on titration curves.',
        maxWords: 200,
        order: 2
    }
];

const DEFAULT_DISCUSSION_QUESTIONS_MAGNESIUM_OXIDE: DiscussionQuestion[] = [
    {
        id: 'mgo-disc-1',
        question: 'What is the purpose of rubbing the magnesium ribbon with sandpaper before using it?',
        expectedKeyPoints: ['Remove the oxide layer on the surface of magnesium', 'Mass of pure magnesium can be measured accurately'],
        guidanceNotes: 'Expected answer: To remove the oxide layer on the surface of magnesium so that the mass of pure magnesium can be measured accurately. Award full marks if both the removal of the oxide layer and the accuracy of mass measurement are mentioned.',
        maxWords: 80,
        order: 0
    },
    {
        id: 'mgo-disc-2',
        question: 'Name the white fumes that are produced.',
        expectedKeyPoints: ['Magnesium oxide', 'MgO'],
        guidanceNotes: 'Expected answer: Magnesium oxide, MgO. Accept either the name or the chemical formula; award full marks if both are stated.',
        maxWords: 40,
        order: 1
    },
    {
        id: 'mgo-disc-3',
        question: 'Why is the lid lifted briefly during heating?',
        expectedKeyPoints: ['Allow oxygen from the air to enter', 'Magnesium burns completely'],
        guidanceNotes: 'Expected answer: To allow oxygen from the air to enter so that the magnesium burns completely. Look for the link between letting air/oxygen in and ensuring complete combustion.',
        maxWords: 80,
        order: 2
    },
    {
        id: 'mgo-disc-4',
        question: 'Why must heating, cooling and weighing be repeated until a constant mass?',
        expectedKeyPoints: ['All the magnesium has completely reacted with oxygen', 'Gives an accurate result'],
        guidanceNotes: 'Expected answer: To ensure all the magnesium has completely reacted with oxygen, giving an accurate result. Reward students who explain that constant mass indicates the reaction is complete.',
        maxWords: 100,
        order: 3
    }
];

const DEFAULT_DISCUSSION_QUESTIONS_COPPER_OXIDE: DiscussionQuestion[] = [
    {
        id: 'cuo-disc-1',
        question: 'What is the purpose of using zinc granules and hydrochloric acid in this activity?',
        expectedKeyPoints: ['Generate hydrogen gas', 'Hydrogen acts as the reducing agent', 'Reduces copper(II) oxide to copper'],
        guidanceNotes: 'Expected answer: To generate hydrogen gas, which acts as the reducing agent to reduce copper(II) oxide to copper. Award full marks when the student names hydrogen as the gas produced AND explains its role as a reducing agent.',
        maxWords: 80,
        order: 0
    },
    {
        id: 'cuo-disc-2',
        question: 'Why does the hydrogen gas need to flow continuously for a while before starting the heating process?',
        expectedKeyPoints: ['Displace all the air inside the glass tube', 'Prevent an explosion when the tube is heated', 'Avoid hydrogen-oxygen mixture igniting'],
        guidanceNotes: 'Expected answer: To displace all the air inside the glass tube, preventing an explosion when the tube is heated. Reward students who explicitly link displacement of air/oxygen to back-flash safety.',
        maxWords: 100,
        order: 1
    },
    {
        id: 'cuo-disc-3',
        question: 'Why is the hydrogen gas allowed to flow until the product of heating is at room temperature?',
        expectedKeyPoints: ['Prevent the hot copper from being re-oxidised', 'Oxygen in the air would oxidise copper back to CuO'],
        guidanceNotes: 'Expected answer: To prevent the hot copper from being re-oxidised by oxygen in the air. Look for understanding that hot copper is reactive with atmospheric O₂ and that the H₂ flow blankets it until it is cool enough.',
        maxWords: 100,
        order: 2
    },
    {
        id: 'cuo-disc-4',
        question: 'Why do the heating, cooling and weighing processes need to be repeated until a constant mass is obtained?',
        expectedKeyPoints: ['All the copper(II) oxide has been completely reduced', 'Gives an accurate mass', 'Confirms reaction is complete'],
        guidanceNotes: 'Expected answer: To ensure all the copper(II) oxide has been completely reduced to copper, giving an accurate mass. Reward students who explain that an unchanging mass shows no more oxygen is being removed.',
        maxWords: 100,
        order: 3
    }
];

const DEFAULT_DISCUSSION_QUESTIONS_BALANCED_EQUATIONS: DiscussionQuestion[] = [
    {
        id: 'be-disc-1',
        question: 'What is the chemical equation for Part A (heating CuCO₃)?',
        expectedKeyPoints: ['CuCO3 → CuO + CO2', 'green CuCO3 turns black (CuO)', 'CO2 gas turns limewater milky'],
        guidanceNotes: 'Expected answer: CuCO₃(s) → CuO(s) + CO₂(g). Full marks if the student writes the balanced equation correctly AND notes the colour change (green to black) and the limewater test for CO₂.',
        maxWords: 80,
        order: 0
    },
    {
        id: 'be-disc-2',
        question: 'What is the chemical equation for Part B (HCl + NH₃)?',
        expectedKeyPoints: ['HCl + NH3 → NH4Cl', 'White smoke of ammonium chloride', 'Solid product from two gases'],
        guidanceNotes: 'Expected answer: HCl(g) + NH₃(g) → NH₄Cl(s). Full marks if the student writes the balanced equation AND identifies the white smoke as solid NH₄Cl.',
        maxWords: 80,
        order: 1
    },
    {
        id: 'be-disc-3',
        question: 'What is the chemical equation for Part C (Pb(NO₃)₂ + KI)?',
        expectedKeyPoints: ['Pb(NO3)2 + 2KI → PbI2 + 2KNO3', 'Bright yellow precipitate', 'Lead(II) iodide is insoluble'],
        guidanceNotes: 'Expected answer: Pb(NO₃)₂(aq) + 2KI(aq) → PbI₂(s) + 2KNO₃(aq). Full marks if the balanced equation has correct coefficients (2 in front of KI and KNO₃) AND the precipitate is identified as yellow PbI₂.',
        maxWords: 100,
        order: 2
    }
];

function getDefaultDiscussionQuestions(experimentType: ExperimentType): DiscussionQuestion[] {
    if (experimentType === 'magnesiumOxide') return DEFAULT_DISCUSSION_QUESTIONS_MAGNESIUM_OXIDE;
    if (experimentType === 'copperOxide') return DEFAULT_DISCUSSION_QUESTIONS_COPPER_OXIDE;
    if (experimentType === 'balancedEquations') return DEFAULT_DISCUSSION_QUESTIONS_BALANCED_EQUATIONS;
    return DEFAULT_DISCUSSION_QUESTIONS;
}

function DiscussionStep({
    enabled,
    setEnabled,
    questions,
    setQuestions,
    experimentType,
    onBack,
}: {
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    questions: DiscussionQuestion[];
    setQuestions: (questions: DiscussionQuestion[]) => void;
    experimentType: ExperimentType;
    onBack: () => void;
}) {
    const templateQuestions = getDefaultDiscussionQuestions(experimentType);
    const handleLoadTemplate = () => {
        setQuestions(templateQuestions.map(q => ({ ...q, id: Date.now().toString() + Math.random().toString() })));
    };

    return (
        <div className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white font-space tracking-tight mb-1">
                        Post-Lab Discussion
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Evaluate students' critical thinking and analysis with AI-assisted grading.
                    </p>
                </div>
                <label className="flex items-center gap-3 cursor-pointer bg-white dark:bg-white/5 p-3 rounded-xl border border-slate-200 dark:border-violet-500/15 hover:border-blue-300 dark:hover:border-violet-500/30 transition-all shrink-0">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-violet-500 focus:ring-blue-500 dark:focus:ring-violet-500"
                    />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 font-space whitespace-nowrap">
                        Enable Auto-Grading
                    </span>
                </label>
            </div>

            {enabled && (
                <div className="space-y-5 animate-in fade-in zoom-in-95 duration-300">
                    {questions.length === 0 && (
                        <div className="p-8 border-2 border-dashed border-slate-300 dark:border-violet-500/15 rounded-2xl bg-slate-50 dark:bg-white/[0.02] flex flex-col items-center text-center group transition-all hover:border-blue-400 dark:hover:border-violet-500/30">
                            <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-violet-900/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                <MessageSquare className="w-7 h-7 text-blue-500 dark:text-violet-400" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 font-space">No Discussion Questions</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 max-w-sm">
                                Add qualitative questions — the AI will grade responses against your expected key points.
                            </p>
                            <button
                                onClick={handleLoadTemplate}
                                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 dark:hover:from-violet-500 dark:hover:to-cyan-400 text-white rounded-xl font-semibold text-sm font-space shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25 hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <BookOpen className="w-4 h-4" />
                                Load Template ({templateQuestions.length} Questions)
                            </button>
                        </div>
                    )}

                    <div className="bg-white dark:bg-[#0d0d1a] rounded-2xl border border-slate-200 dark:border-violet-500/10 overflow-hidden shadow-sm">
                        <DiscussionQuestionEditor
                            questions={questions}
                            onChange={setQuestions}
                        />
                    </div>
                </div>
            )}

            {!enabled && (
                <div className="text-center py-16 px-4 rounded-2xl bg-gray-50/50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 border-dashed animate-in fade-in duration-300">
                    <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4 border border-gray-200 dark:border-gray-700">
                        <ShieldAlert className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Post-Lab Evaluation Offline</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                        Discussion questions are disabled. Students will receive final grades without submitting written essays.
                    </p>
                </div>
            )}
        </div>
    );
}
